// utils/htmlSanitizer.ts
// HTML sanitization utility to prevent XSS attacks

import DOMPurify from 'dompurify';

/**
 * Configuration for DOMPurify
 * Defines which HTML tags and attributes are allowed
 */
const SANITIZER_CONFIG = {
    // Allowed HTML tags
    ALLOWED_TAGS: [
        // Text formatting
        'p', 'br', 'strong', 'em', 'u', 's', 'span',
        // Headings
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        // Lists
        'ul', 'ol', 'li',
        // Blockquotes
        'blockquote',
        // Links
        'a',
        // Images
        'img',
        // Videos (iframe for embeds)
        'iframe',
        // Divs for structure
        'div',
    ],

    // Allowed attributes
    ALLOWED_ATTR: [
        // Links
        'href', 'target', 'rel',
        // Images
        'src', 'alt', 'width', 'height',
        // Styling (limited)
        'class', 'style',
        // Iframes (for video embeds)
        'frameborder', 'allowfullscreen',
    ],

    // Additional security settings
    ALLOW_DATA_ATTR: false, // No data-* attributes
    KEEP_CONTENT: true, // Keep content of removed tags
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - Raw HTML string from editor
 * @returns Sanitized HTML safe for storage and display
 */
export const sanitizeHtml = (html: string): string => {
    if (!html || typeof html !== 'string') {
        return '';
    }

    // Use DOMPurify to clean the HTML
    const clean = DOMPurify.sanitize(html, SANITIZER_CONFIG);

    // Additional post-processing
    return postProcessHtml(clean);
};

/**
 * Post-processes sanitized HTML for consistency
 * @param html - Sanitized HTML
 * @returns Cleaned and normalized HTML
 */
const postProcessHtml = (html: string): string => {
    let processed = html;

    // Remove empty paragraphs
    processed = processed.replace(/<p>\s*<\/p>/g, '');
    processed = processed.replace(/<p><br><\/p>/g, '');

    // Normalize link targets to open in new tab
    processed = processed.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');

    // Remove excessive line breaks
    processed = processed.replace(/(<br\s*\/?>\s*){3,}/g, '<br><br>');

    // Trim whitespace
    processed = processed.trim();

    return processed;
};

/**
 * Strips all HTML tags and returns plain text
 * Useful for generating article previews and captions
 * @param html - HTML string
 * @returns Plain text content
 */
export const stripHtmlTags = (html: string): string => {
    if (!html) return '';

    // Remove HTML tags
    const text = html.replace(/<[^>]*>/g, ' ');

    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    const decoded = textarea.value;

    // Normalize whitespace
    return decoded.replace(/\s+/g, ' ').trim();
};

/**
 * Validates HTML content for security issues
 * Returns true if content is safe, false if suspicious
 * @param html - HTML string to validate
 * @returns Validation result
 */
export const validateHtmlSecurity = (html: string): {
    isValid: boolean;
    issues: string[];
} => {
    const issues: string[] = [];

    if (!html) {
        return { isValid: true, issues: [] };
    }

    // Check for script tags (should be caught by sanitizer, but double-check)
    if (/<script/i.test(html)) {
        issues.push('Script tags detected');
    }

    // Check for javascript: protocol
    if (/javascript:/i.test(html)) {
        issues.push('JavaScript protocol detected in links');
    }

    // Check for event handlers
    if (/on\w+\s*=/i.test(html)) {
        issues.push('Event handlers detected');
    }

    // Check for data URIs (can be used for phishing)
    if (/data:text\/html/i.test(html)) {
        issues.push('Data URI detected');
    }

    return {
        isValid: issues.length === 0,
        issues,
    };
};

/**
 * Creates a safe preview of HTML content
 * Limits length and strips potentially problematic elements
 * @param html - Full HTML content
 * @param maxLength - Maximum characters in preview (default: 200)
 * @returns Safe preview text
 */
export const createSafePreview = (html: string, maxLength: number = 200): string => {
    // Strip all HTML tags
    const plainText = stripHtmlTags(html);

    // Truncate to maxLength
    if (plainText.length <= maxLength) {
        return plainText;
    }

    // Find the last complete word before maxLength
    const truncated = plainText.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > 0) {
        return truncated.substring(0, lastSpace) + '...';
    }

    return truncated + '...';
};

/**
 * Estimates reading time based on HTML content
 * Assumes average reading speed of 200 words per minute
 * @param html - HTML content
 * @returns Reading time in minutes
 */
export const estimateReadingTime = (html: string): number => {
    const plainText = stripHtmlTags(html);
    const wordCount = plainText.split(/\s+/).filter(word => word.length > 0).length;
    const wordsPerMinute = 200; // Average reading speed

    return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Type guard to check if content is HTML
 * @param content - String content
 * @returns True if content contains HTML tags
 */
export const isHtmlContent = (content: string): boolean => {
    if (!content) return false;
    return /<[^>]+>/.test(content);
};

// Named exports for better tree-shaking
const htmlSanitizerUtils = {
    sanitizeHtml,
    stripHtmlTags,
    validateHtmlSecurity,
    createSafePreview,
    estimateReadingTime,
    isHtmlContent,
};

export default htmlSanitizerUtils;